import { defineStore } from 'pinia';
import { useStore as useProductStore } from './product';
import { useStore as useShapeStore } from './shape';
import {
  getCommercialRemarkNotifications,
  getEmptyPositionNotifications,
  getOutOfBoundariesNotifications,
  getSmallDetailsNotifications,
  getGradientFileNotifications,
  getRecommendedPrintPositionNotifications,
} from '@/utils/validationHelper';
import { constructArtworkValidationPipeline } from '@/web-components/import-wizard-v2/validators';
import { NotificationResult } from '@/web-components/import-wizard-v2/typings/notification';
import { NotificationSnapshot } from '@/typings/historyV2';
import { RECOMMENDED_PRINT_POSITION_FLOW_ACTIVE } from '@/constants/featureFlags';

export const useStore = defineStore({
  id: 'notification',

  state: () => {
    return {
      notifications: [] as NotificationResult[],
    };
  },
  getters: {
    hasWarningNotifications: (state): boolean => {
      return (
        state.notifications.filter((x) => x.validatorColor === 'Orange')
          .length > 0
      );
    },
    hasRedNotifications: (state): boolean => {
      return (
        state.notifications.filter((x) => x.validatorColor === 'Red').length > 0
      );
    },
    redAndOrangeNotifications: (state): NotificationResult[] => {
      const validatorColorOrder = ['Red', 'Orange'];
      return state.notifications
        .filter(
          (x) => x.validatorColor === 'Orange' || x.validatorColor === 'Red'
        )
        .sort(
          (a, b) =>
            validatorColorOrder.indexOf(a.validatorColor) -
            validatorColorOrder.indexOf(b.validatorColor)
        );
    },
  },
  actions: {
    runValidators() {
      // reset notifications and run all validators
      this.notifications = [];

      const productStore = useProductStore();
      const shapeStore = useShapeStore();

      const commertialRemarkNotifications = getCommercialRemarkNotifications(
        productStore.unavailablePrintPositions,
        productStore.data
      );

      const pipelineNotifications = constructArtworkValidationPipeline(
        productStore.data,
        productStore.unavailablePrintPositions,
        shapeStore.shapes,
        shapeStore.positionShapeMap
      );

      const emptyPositionNotifications = getEmptyPositionNotifications(
        productStore.unavailablePrintPositions,
        shapeStore.positionShapeMap
      );

      const smallDetailsNotifications = getSmallDetailsNotifications(
        shapeStore.shapes,
        shapeStore.positionShapeMap,
        productStore.unavailablePrintPositions
      );

      const outOfBoundariesNotifications = getOutOfBoundariesNotifications(
        shapeStore.shapes,
        shapeStore.positionShapeMap,
        productStore.unavailablePrintPositions
      );

      const gradientFileNotifications = getGradientFileNotifications(
        shapeStore.shapes,
        shapeStore.positionShapeMap,
        productStore.unavailablePrintPositions
      );
      const recommendedPrintPositionNotifications =
        RECOMMENDED_PRINT_POSITION_FLOW_ACTIVE
          ? getRecommendedPrintPositionNotifications(
              shapeStore.shapes,
              shapeStore.positionShapeMap,
              productStore.unavailablePrintPositions
            )
          : [];

      this.notifications = [
        ...commertialRemarkNotifications,
        ...pipelineNotifications,
        ...emptyPositionNotifications,
        ...smallDetailsNotifications,
        ...outOfBoundariesNotifications,
        ...gradientFileNotifications,
        ...recommendedPrintPositionNotifications,
      ];
    },
    updateNotificationStateFromSnapshot(snapshot: NotificationSnapshot) {
      this.$patch((state) => {
        if (snapshot !== undefined) {
          state.notifications = snapshot.notifications;
        }
      });
    },
  },
});
